import { useMemo } from 'react';
import { Text, Box, Group, Tooltip } from '@mantine/core';
import { AxisWeight } from '../../types/tasteProfile';

type PreferencePillStripProps = {
  title: string;
  keys: readonly string[];
  labels: Record<string, string>;
  weights: Record<string, AxisWeight>;
  preferredKeys: string[];
  avoidedKeys: string[];
};

const NET_THRESHOLD = 5;

const classify = (
  key: string,
  preferred: string[],
  avoided: string[],
  w: AxisWeight | undefined
): 'preferred' | 'avoided' | 'neutral' | 'undiscovered' => {
  if (preferred.includes(key)) return 'preferred';
  if (avoided.includes(key)) return 'avoided';
  if (!w) return 'undiscovered';
  const net = w.posWeight - w.negWeight;
  if (net >= NET_THRESHOLD) return 'preferred';
  if (net <= -NET_THRESHOLD) return 'avoided';
  return 'neutral';
};

const chipStyle = (
  key: string,
  preferred: string[],
  avoided: string[],
  w: AxisWeight | undefined,
  maxAbs: number
): { bg: string; fg: string } => {
  const cls = classify(key, preferred, avoided, w);

  if (cls === 'preferred' || cls === 'avoided') {
    const ratio = w ? Math.abs(w.posWeight - w.negWeight) / Math.max(maxAbs, 0.01) : 0;
    const color = cls === 'preferred' ? 'green' : 'red';
    const shade = ratio >= 0.7 ? 6 : ratio >= 0.3 ? 4 : 2;
    const fg = shade >= 4 ? 'white' : `var(--mantine-color-${color}-7)`;
    return { bg: `var(--mantine-color-${color}-${shade})`, fg };
  }
  if (cls === 'neutral') {
    return { bg: 'var(--mantine-color-gray-2)', fg: 'var(--mantine-color-gray-6)' };
  }
  return { bg: 'var(--mantine-color-gray-1)', fg: 'var(--mantine-color-gray-5)' };
};

const BarTooltip = ({ label, w, maxVal }: { label: string; w: AxisWeight | undefined; maxVal: number }) => {
  if (!w || (w.posWeight === 0 && w.negWeight === 0)) return <>No data</>;
  return (
    <Box style={{ fontSize: 11, fontWeight: 500 }}>
      <div style={{ fontWeight: 700, marginBottom: 3 }}>{label}</div>
      {[
        { name: 'Preferred', value: w.posWeight, color: 'var(--mantine-color-green-5)' },
        { name: 'Avoided', value: w.negWeight, color: 'var(--mantine-color-red-4)' },
      ].map(({ name, value, color }) => (
        <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span style={{ width: 52, flexShrink: 0 }}>{name}</span>
          <div style={{ width: 60, height: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ width: `${(value / maxVal) * 100}%`, height: '100%', backgroundColor: color, borderRadius: 3 }} />
          </div>
        </div>
      ))}
    </Box>
  );
};

export const PreferencePillStrip = ({ title, keys, labels, weights, preferredKeys, avoidedKeys }: PreferencePillStripProps) => {
  /**
   * Alphabetical by the LABEL, not the key — "soup_stew" renders as
   * "Soup / stew" and sorting on the key would look scrambled to a reader.
   *
   * Sorted here rather than by reordering the *_KEYS arrays, because those
   * mirror the backend vocabularies and are easier to diff against them in
   * source order. Ordering is a display concern.
   *
   * Colour carries the actual signal, so the order only has to make a given
   * pill findable. A user scanning 15 techniques for "Grill" can jump to it;
   * previously they had to read the whole strip. localeCompare so "Sauté"
   * sorts where a reader expects.
   */
  const orderedKeys = useMemo(
    () =>
      [...keys].sort((a, b) =>
        (labels[a] || a).localeCompare(labels[b] || b, undefined, { sensitivity: 'base' })
      ),
    [keys, labels]
  );

  const maxAbs = Math.max(
    ...keys.map((key) => {
      const w = weights[key];
      return w ? Math.abs(w.posWeight - w.negWeight) : 0;
    }),
    0.01
  );

  const maxVal = Math.max(
    ...keys.map((key) => {
      const w = weights[key];
      return w ? Math.max(w.posWeight, w.negWeight) : 0;
    }),
    0.01
  );

  return (
    <Box>
      <Text size="md" fw={600} mb="xs">
        {title}
      </Text>
      <Box style={{ display: 'inline-flex', backgroundColor: 'var(--mantine-color-gray-0)', borderRadius: 8, padding: '6px 12px' }} mb="sm">
        <Group gap="md">
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-green-6)' }} />
            <Text size="xs" c="dimmed">Preferred</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-red-6)' }} />
            <Text size="xs" c="dimmed">Avoided</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-gray-2)' }} />
            <Text size="xs" c="dimmed">Neutral</Text>
          </Group>
          <Group gap={4}>
            <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: 'var(--mantine-color-gray-1)' }} />
            <Text size="xs" c="dimmed">Undiscovered</Text>
          </Group>
        </Group>
      </Box>
      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 6px' }}>
        {orderedKeys.map((key) => {
          const { bg, fg } = chipStyle(key, preferredKeys, avoidedKeys, weights[key], maxAbs);
          const label = labels[key] || key;

          return (
            <Tooltip key={key} label={<BarTooltip label={label} w={weights[key]} maxVal={maxVal} />} withArrow events={{ hover: true, focus: true, touch: true }}>
              <Box
                style={{
                  backgroundColor: bg,
                  color: fg,
                  borderRadius: 20,
                  padding: '8px 16px',
                  textAlign: 'center',
                  fontSize: 13,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};
